import type { ReactNode } from "react";
import { allDocPages } from "./content";
import DocsLayout from "./layout";

const markdown = `
The CloudQuote REST API follows a small set of consistent conventions. Once you've made one call, the rest of the platform will feel familiar.

## Base URL

All data endpoints share a common host:

\`\`\`
https://api.cloudquote.io
\`\`\`

The NewsRPM service has its own host:

\`\`\`
https://api.newsrpm.com
\`\`\`

## Authentication

Every request must carry a credential. The simplest form is a private key in the \`Authorization\` header:

\`\`\`
Authorization: privateKey YOUR_KEY_HERE
\`\`\`

Browser-originated requests should use a public key with a whitelisted domain instead — see [Authentication](/docs/authentication).

## Response formats

Most endpoints can return data in any of three formats. Pick the format with the file extension on the path:

- \`/getQuote.json\` — JSON (default; recommended for new integrations).
- \`/getQuote.csv\` — CSV (great for spreadsheets and ETL pipelines).
- \`/getQuote.xml\` — XML (for legacy systems).

Schemas are stable across formats.

## Common request shape

Most endpoints accept symbol-based parameters:

\`\`\`
GET /fcon/getQuote.json?symbol=AAPL
GET /fcon/getQuote.json?symbols=AAPL,MSFT,GOOGL
GET /fcon/getPriceHistory.json?symbol=TSLA&startDate=2025-01-01&endDate=2025-05-30&interval=1d
\`\`\`

Use comma-separated lists when you need multiple symbols in a single call — it's faster than fanning out.

## Error handling

Errors come back as JSON with an HTTP status code that matches the situation:

| Status | Meaning |
| :---- | :---- |
| \`400\` | Malformed request — check parameter names and types. |
| \`401\` | Missing or invalid credential. |
| \`403\` | Authenticated but not entitled to the requested data. |
| \`404\` | Symbol or resource not found. |
| \`429\` | Rate limit exceeded — back off and retry. |
| \`500\` | Platform error — safe to retry with exponential backoff. |

A typical error body:

\`\`\`json
{
  "error": "Rate limit exceeded for getQuote. Limit: 50/min."
}
\`\`\`

## Rate limits

Rate limits are per-credential and per-endpoint. They are visible on your **API Dashboard → Usage** view, and exceeding them returns a \`429\` with a structured body. Build clients that respect the limit by:

- Batching symbols where possible.
- Caching responses for as long as your use case allows (real-time = seconds; historical = hours or longer).
- Reading the \`X-RateLimit-Remaining\` header before firing the next request.

## Entitlements

Different subscriptions cover different endpoints. If you call an endpoint that isn't covered, you'll get a \`403\` describing what's missing. Add the endpoint to your plan from the API Dashboard or [contact us](/contact).

## CORS

JSON endpoints emit standard CORS response headers. They will be honored if the request originates from a whitelisted domain *and* does not include a private key. See [Front-End Integration](/docs/frontend-integration) for the full set of rules.

## Discovery

Each endpoint exposes a machine-readable specification via \`getSpec\`:

\`\`\`bash
curl 'https://api.cloudquote.io/fcon/getQuote.json?action=getSpec'
\`\`\`

That returns the endpoint's parameter schema and example responses — handy when you're auto-generating client code or registering tools for the [Chat API](/docs/chat-api) or [MCP](/docs/mcp).

## Where to go next

- For the news endpoints specifically, see [NewsRPM News API](/docs/newsrpm-api).
- To search across all data domains, see the [Search API](/docs/search-api).
- For higher-level, conversational access, see the [Chat API](/docs/chat-api).
`;

export default function RestApiPage(): ReactNode {
  const page = allDocPages.find(p => p.slug === "rest-api")!;
  return <DocsLayout page={page} markdown={markdown}/>;
}
